import React, { useMemo, useState } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Switch,
  Space,
  Tag,
  Popconfirm,
  Result,
  App,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { GeoRule, RuleStatus } from './types';
import {
  useDeleteRuleMutation,
  useGeoRules,
  useUpdateRuleMutation,
} from './queries';
import { RuleFormModal } from './RuleFormModal';
import { RuleDetailsDrawer } from './RuleDetailsDrawer';
import { formatDateTime } from '../../utils/format';
import { ApiError } from '../../api/types';

const SCOPE_COLORS: Record<string, string> = {
  ip: 'geekblue',
  cidr: 'purple',
  account: 'orange',
  country: 'cyan',
};

export const RulesPage: React.FC = () => {
  const { message } = App.useApp();
  const { data: rules, isLoading, error, refetch } = useGeoRules();
  const updateMutation = useUpdateRuleMutation();
  const deleteMutation = useDeleteRuleMutation();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | RuleStatus>('all');

  // Modal / Drawer state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<GeoRule | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewingRule, setViewingRule] = useState<GeoRule | null>(null);

  // Per-row loading state
  const [pendingStatusRuleId, setPendingStatusRuleId] = useState<number | null>(
    null
  );
  const [deletingRuleId, setDeletingRuleId] = useState<number | null>(null);

  const filteredRules = useMemo(() => {
    if (!rules) return [];
    const query = searchQuery.trim().toLowerCase();
    return rules.filter((rule) => {
      if (statusFilter !== 'all' && rule.status !== statusFilter) {
        return false;
      }
      if (query) {
        const matchesName = rule.name.toLowerCase().includes(query);
        const matchesValue = rule.value.toLowerCase().includes(query);
        if (!matchesName && !matchesValue) {
          return false;
        }
      }
      return true;
    });
  }, [rules, searchQuery, statusFilter]);

  const handleStatusToggle = async (rule: GeoRule, checked: boolean) => {
    const nextStatus: RuleStatus = checked ? 'active' : 'inactive';
    setPendingStatusRuleId(rule.id);
    try {
      await updateMutation.mutateAsync({
        id: rule.id,
        input: {
          name: rule.name,
          scope: rule.scope,
          value: rule.value,
          action: rule.action,
          priority: rule.priority,
          status: nextStatus,
        },
      });
      message.success(`Rule "${rule.name}" is now ${nextStatus}`);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        message.error(err.message);
      } else if (err instanceof Error) {
        message.error(err.message);
      } else {
        message.error('Failed to update rule status');
      }
    } finally {
      setPendingStatusRuleId(null);
    }
  };

  const handleDelete = async (rule: GeoRule) => {
    setDeletingRuleId(rule.id);
    try {
      await deleteMutation.mutateAsync(rule.id);
      message.success(`Rule "${rule.name}" deleted`);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        message.error(err.message);
      } else if (err instanceof Error) {
        message.error(err.message);
      } else {
        message.error('Failed to delete rule');
      }
    } finally {
      setDeletingRuleId(null);
    }
  };

  const openCreateModal = () => {
    setEditingRule(null);
    setModalOpen(true);
  };

  const openEditModal = (rule: GeoRule) => {
    setEditingRule(rule);
    setModalOpen(true);
  };

  const openViewDrawer = (rule: GeoRule) => {
    setViewingRule(rule);
    setDrawerOpen(true);
  };

  if (error) {
    return (
      <Result
        status="error"
        title="Failed to load rules"
        subTitle={
          error instanceof Error ? error.message : 'An unexpected error occurred'
        }
        extra={
          <Button type="primary" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const columns: ColumnsType<GeoRule> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <span
          className="font-medium text-slate-800 cursor-pointer hover:text-blue-600"
          onClick={() => openViewDrawer(record)}
        >
          {name}
        </span>
      ),
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      render: (scope: string) => (
        <Tag color={SCOPE_COLORS[scope] || 'default'}>
          {scope.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (val: string) => (
        <code className="bg-slate-100 px-1 py-0.5 rounded font-mono text-xs text-slate-700">
          {val}
        </code>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action: string) => (
        <Tag color={action === 'allow' ? 'green' : 'red'}>
          {action.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: RuleStatus, record) => (
        <Switch
          checked={status === 'active'}
          loading={pendingStatusRuleId === record.id}
          disabled={pendingStatusRuleId === record.id}
          onChange={(checked) => handleStatusToggle(record, checked)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (updatedAt: string) => formatDateTime(updatedAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            onClick={() => openViewDrawer(record)}
          >
            View
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => openEditModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title={`Delete rule "${record.name}"?`}
            description="Are you sure you want to delete this rule?"
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
            disabled={deletingRuleId === record.id}
          >
            <Button
              type="link"
              danger
              size="small"
              loading={deletingRuleId === record.id}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search name or value"
            prefix={<SearchOutlined className="text-slate-400" />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            style={{ width: 220 }}
          />
          <Select
            value={statusFilter}
            onChange={(val) => setStatusFilter(val)}
            style={{ width: 120 }}
            options={[
              { value: 'all', label: 'All' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
          />
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
        >
          Create rule
        </Button>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredRules}
        loading={isLoading}
        scroll={{ x: 800 }}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total) => `Total ${total} rules`,
        }}
      />

      <RuleFormModal
        open={modalOpen}
        rule={editingRule}
        onClose={() => {
          setModalOpen(false);
          setEditingRule(null);
        }}
      />

      <RuleDetailsDrawer
        open={drawerOpen}
        rule={viewingRule}
        onClose={() => {
          setDrawerOpen(false);
          setViewingRule(null);
        }}
      />
    </div>
  );
};
